// requestsKinds — the RequestKind sets and filter options the Requests page
// switches on.
//
// PURE ON PURPOSE, the same discipline lib/sentItemIds.ts keeps: the only import
// is a TYPE (erased at compile under verbatimModuleSyntax), so nothing in this
// module's graph reads `import.meta.env` and these lists can be asserted with a
// bare `npx tsx --test` rather than under the module-mock flag.
//
// That matters because these sets are a REGISTRY MIRROR. Each one has to gain a
// row every time a new signable document type ships, and a kind missing from one
// of them fails quietly rather than loudly: a signing document missing from
// SIGNING_KINDS renders a key-value dump instead of its signer roster, and one
// missing from CANCEL_RDA_KINDS offers the generic cancel, which refuses an
// intake-phase row with "no active submission". requestsKinds.test.ts is where
// the next omission gets caught.

import type { RequestKind, RequestStatus } from '@/lib/requestsClient';

/** The RequestKinds that are DocuSeal signature documents — for these the kind
 *  IS the signature record's documentType, so the detail drawer can offer the
 *  current-state document download. RV / intake kinds are forms, not documents. */
export const DOCUMENT_KINDS: ReadonlySet<RequestKind> = new Set<RequestKind>([
  'lease',
  'adoption_addendum',
  'condition_report',
  'moveout_funds_direction',
  'payment_plan',
  'manual_document',
  'property_management_agreement',
  'leaseholder_change',
  'leaseholder_removal',
  'animal_addendum',
  'animal_addendum_paid',
]);

/** A value of the Requests page's Type filter: 'all', or the kind an option is named for. */
export type KindFilterValue = 'all' | RequestKind;

/**
 * The Type filter's options. One option can stand for more than one kind — see
 * KIND_FILTER_MEMBERS — so filter rows with `matchesKindFilter`, never with
 * `row.kind === value`.
 */
export const KIND_FILTERS: Array<{ value: KindFilterValue; label: string }> = [
  { value: 'all', label: 'All types' },
  { value: 'rental_verification', label: 'Rental Verification' },
  { value: 'intake_designation', label: 'Designation' },
  { value: 'intake_yard_care', label: 'Yard care' },
  { value: 'intake_animals', label: 'Animals' },
  { value: 'lease', label: 'Lease' },
  { value: 'adoption_addendum', label: 'Addendum' },
  { value: 'condition_report', label: 'Condition report' },
  { value: 'moveout_funds_direction', label: 'Move-out funds' },
  { value: 'payment_plan', label: 'Payment plan' },
  { value: 'manual_document', label: 'Manual document' },
  { value: 'owner_intake', label: 'Owner intake (property management)' },
  { value: 'property_management_agreement', label: 'Property management agreement' },
  { value: 'leaseholder_change', label: 'Leaseholder change' },
  { value: 'leaseholder_removal', label: 'Leaseholder removal' },
  // WEL-172 (Andrea): ONE "Animal addendum" option for both animal addenda —
  // the accommodation one (`animal_addendum`) and the household new-animal one
  // (`animal_addendum_paid`). They stay separate kinds with separate flows;
  // staff just look for them under one name. KIND_FILTER_MEMBERS says what
  // this option matches.
  { value: 'animal_addendum', label: 'Animal addendum' },
  { value: 'owner_contact_addendum', label: 'Owner Contact Addendum' },
  { value: 'city_utility_agreement', label: 'City utilities agreement' },
  { value: 'w9', label: 'Owner W-9' },
  { value: 'vendor_w9', label: 'Vendor W-9' },
];

/**
 * The options that stand for MORE than their own kind. An option not listed
 * here matches exactly its own kind.
 */
export const KIND_FILTER_MEMBERS: ReadonlyMap<RequestKind, ReadonlySet<RequestKind>> = new Map<
  RequestKind,
  ReadonlySet<RequestKind>
>([['animal_addendum', new Set<RequestKind>(['animal_addendum', 'animal_addendum_paid'])]]);

/**
 * Filter values that are no longer options, and the option that replaced
 * each. A link written before an option was folded into another still opens
 * on the option that now covers it, rather than on "All types".
 */
export const RETIRED_KIND_FILTERS: ReadonlyMap<string, KindFilterValue> = new Map<string, KindFilterValue>([
  // WEL-172: "Animal addendum (new animal)" was folded into "Animal addendum".
  ['animal_addendum_paid', 'animal_addendum'],
]);

/** Whether a row of `kind` shows under the Type filter `filter`. */
export function matchesKindFilter(filter: KindFilterValue, kind: RequestKind): boolean {
  if (filter === 'all') return true;
  const members = KIND_FILTER_MEMBERS.get(filter);
  return members ? members.has(kind) : kind === filter;
}

/**
 * The Type filter a `?kind=` URL value selects: an option's own value, the
 * option that replaced a retired value, or 'all' for anything else (missing,
 * blank, or unknown — a mistyped link shows every row rather than none).
 */
export function kindFilterFromParam(raw: string | null | undefined): KindFilterValue {
  const value = (raw ?? '').trim();
  if (!value) return 'all';
  const replaced = RETIRED_KIND_FILTERS.get(value);
  if (replaced) return replaced;
  return KIND_FILTERS.some((f) => f.value === value) ? (value as KindFilterValue) : 'all';
}

/**
 * The W-9 kinds (owner + vendor). Both rows are REDACTED server-side and
 * view-only: no roster, no resend, no document download in the drawer — so
 * neither belongs in DOCUMENT_KINDS or SIGNING_KINDS. A completed one offers
 * "View W-9", which opens the signed PDF through the audited, five-minute link.
 */
export const W9_KINDS: ReadonlySet<RequestKind> = new Set<RequestKind>(['w9', 'vendor_w9']);

export function isW9Kind(kind: RequestKind): boolean {
  return W9_KINDS.has(kind);
}

/**
 * The version id a W-9 row addresses. The dashboard never carries a W-9's
 * groupId (it derives the public signing page), only `w9:<versionId>` — for
 * the vendor row too (the server keeps the `w9:` prefix for both kinds).
 * `vendor_w9:<versionId>` is tolerated defensively. Anything else is ''.
 */
export function w9VersionIdFromRowId(rowId: string): string {
  for (const prefix of ['w9:', 'vendor_w9:']) {
    if (rowId.startsWith(prefix)) {
      const rest = rowId.slice(prefix.length);
      return rest && !rest.includes(':') ? rest : '';
    }
  }
  return '';
}

/**
 * Signature-backed kinds — canceling these also withdraws the DocuSeal request,
 * and their detail drawer shows the per-signer roster. SINGLE source of truth on
 * the client (mirrors the server's SIGNATURE_KINDS): both the cancel modal's
 * "this withdraws a DocuSeal signing request" warning and the DataSection roster
 * read `isSigningKind`, so they can't drift. WEL-92: `property_management_agreement`
 * (EPMA) is a signing doc, so it belongs here (else an EPMA row misses the cancel
 * warning + renders no signer roster).
 */
export const SIGNING_KINDS = new Set<RequestKind>([
  'lease',
  'adoption_addendum',
  'condition_report',
  'moveout_funds_direction',
  'property_management_agreement',
  // Leaseholder change (docs/leaseholder-change-agreement.md) is a DocuSeal
  // signing doc; its rows carry the full `data.signers` roster, so without this
  // entry the drawer falls back to the generic key-value dump. (The SERVER's
  // SIGNATURE_KINDS hasn't been extended yet — cancel/resend dispatch is an API
  // follow-up — but the roster + cancel warning render correctly from here.)
  'leaseholder_change',
  // Leaseholder removal — the remove-only sibling; same DocuSeal signing shape
  // (carries the full `data.signers` roster), so it belongs here too.
  'leaseholder_removal',
  // Animal addendum (RRA phase 5) — a tenant-only DocuSeal signing doc; the
  // row is deliberately NEUTRAL (standard animal terms, no RRA data).
  'animal_addendum',
  // WEL-153 — the PAID animal addendum (a current tenant adding an animal).
  // Residents + co-signers sign in parallel, then Broker, then Designated
  // Broker, so its rows carry the full `data.signers` roster.
  'animal_addendum_paid',
]);

/** True when a RequestKind is a DocuSeal signing doc (mirrors the server predicate). */
export function isSigningKind(kind: RequestKind): boolean {
  return SIGNING_KINDS.has(kind);
}

/**
 * WEL-48: kinds that own a "Resend intake" action — void the open agreement and
 * restart the recipient-designation intake collection from scratch. The first
 * valid completion of the restarted intake re-locks the selection.
 */
export const RESEND_INTAKE_KINDS = new Set<RequestKind>([
  'moveout_funds_direction',
  // WEL-153: the animal addendum's household form restarts the same way — void
  // the agreement if one is out, then re-collect the animal details.
  'animal_addendum_paid',
  'owner_intake',
]);

/**
 * WEL-157 (Andrea): the Move-out funds (deposit agreement) row gets a Cancel
 * here, not only on Send → Deposit agreement. It goes through the RDA cancel
 * (WEL-125) rather than the generic request cancel because the whole request
 * has to close at once: void the agreement if one is out for signing, stop a
 * not-yet-completed intake, and tell the recipients there is nothing further to
 * do. The generic path only knows how to withdraw a DocuSeal submission and
 * refuses the intake-phase row ("no active submission").
 */
export const CANCEL_RDA_KINDS = new Set<RequestKind>([
  'moveout_funds_direction',
  // WEL-153: same shape — the animal addendum's own cancel closes BOTH phases
  // (form links and a live signing request) in one call, which the generic
  // request cancel cannot do.
  'animal_addendum_paid',
  'owner_intake',
]);

/** The dedicated cancel/resend endpoints are per KIND — this picks the pair. */
export function isAnimalAddendumPaid(kind: RequestKind): boolean {
  return kind === 'animal_addendum_paid';
}

/** WEL-166: the owner-intake phase of a property management agreement — its
 *  resend / cancel go through the owner-intake endpoints. */
export function isOwnerIntake(kind: RequestKind): boolean {
  return kind === 'owner_intake';
}

/**
 * Whether a row should offer the dedicated Cancel at all.
 *
 * The status half is not a tidiness rule, it is a safety one. A FULLY EXECUTED
 * agreement cannot be cancelled — the server refuses it, because there is
 * nothing to void in a signed document — so offering the button on a completed
 * row buys an unhelpful error at best. At worst the operator believes a signed
 * addendum is being withdrawn. A cancelled row has nothing left to cancel
 * either.
 *
 * Both halves have to hold, and the predicate lives here so a test can pin it
 * rather than it living inside a `useMemo` nobody can reach.
 */
export function offersDedicatedCancel(kind: RequestKind, status: RequestStatus): boolean {
  if (!CANCEL_RDA_KINDS.has(kind)) return false;
  return status !== 'completed' && status !== 'cancelled';
}

/**
 * Whether a row offers the GENERIC per-row Cancel (`POST /api/requests/cancel`).
 *
 * Gated on the row's own `canCancel` (WEL-181/182), NOT on `canResend`: a resend
 * needs a recipient email and a usable link, but a request whose signer has no
 * email on file — or a W-9, which is view-only for resend — is just as outstanding
 * and must still be withdrawable. `canResend` is only the fallback for a row from
 * an API that predates `canCancel` (deploy skew). The kinds with their own Cancel
 * (CANCEL_RDA_KINDS) never take the generic path — the generic one would withdraw
 * the signatures but leave the household's form links live.
 */
export function offersGenericCancel(row: {
  kind: RequestKind;
  canCancel?: boolean;
  canResend: boolean;
}): boolean {
  if (CANCEL_RDA_KINDS.has(row.kind)) return false;
  return row.canCancel ?? row.canResend;
}

/**
 * The kinds whose generic Cancel also withdraws a DocuSeal signing request — the
 * Cancel modal says so. A superset of SIGNING_KINDS (which also drives the
 * drawer's signer roster, so it is left alone): it adds the signature-backed
 * kinds that have no roster in the drawer — the payment plan, manual documents,
 * the Owner Contact Addendum, the City utilities agreement and both W-9s.
 */
export const WITHDRAWS_DOCUSEAL_KINDS: ReadonlySet<RequestKind> = new Set<RequestKind>([
  ...SIGNING_KINDS,
  'payment_plan',
  'manual_document',
  'owner_contact_addendum',
  'city_utility_agreement',
  'w9',
  'vendor_w9',
]);

export function withdrawsDocuSeal(kind: RequestKind): boolean {
  return WITHDRAWS_DOCUSEAL_KINDS.has(kind);
}
